import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Grid, Modal } from 'semantic-ui-react';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { LanguageDto, UpsertLanguageDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import { deleteLanguage, findLanguageById, getAllLanguages, searchLanguages } from 'services/languageServices';
import { noop } from 'utils/functionUtils';
import UpsertBillTextForm from 'components/billing/bill-text/UpsertBillTextForm';
import LoaderComponent from 'components/LoaderComponent';
import DeleteActionButton from 'components/DeleteActionButton';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

const initialBillTextSearch: Partial<BillTextsSearchRequest> = {};

interface Props extends MessageSourceProps {
}

interface State {
  foundBillTexts: Array<LanguageDto>,
  billTextsLoaded: boolean,
  language?: UpsertLanguageDto,
  languageId?: number,
  addEditModalOpen: boolean,
  loading: boolean,
  searchValues: Partial<BillTextsSearchRequest>,
  cancelTokenSource: CancelTokenSource,
  isDefault: boolean,
}

interface BillTextsSearchRequest {
  description: string
}

interface BillTextsFieldProps extends FormState {
  values: Partial<BillTextsSearchRequest>
}

class BillTextView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundBillTexts: [],
      billTextsLoaded: false,
      language: undefined,
      languageId: undefined,
      addEditModalOpen: false,
      loading: false,
      searchValues: initialBillTextSearch,
      cancelTokenSource,
      isDefault: false
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;

    this.mounted = false;
    cancelTokenSource.cancel('Operation canceled by user');
  };

  openAddEditModal = (id?: number) => {
    const {cancelTokenSource} = this.state;
    if (id) {
      findLanguageById(id, cancelTokenSource)
          .then(response => this.showModal(id, response))
          .catch(noop);
    }
  };

  showModal = (id?: number, language?: UpsertLanguageDto) => {

    this.setState({
      addEditModalOpen: true,
      language: language,
      languageId: id
    });
  };

  hideModal = () => {

    this.setState({
      addEditModalOpen: false,
      languageId: undefined,
      language: undefined
    });
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;
    const description = searchValues.description;

    const onFinally = () => {
      this.setState({
        billTextsLoaded: true
      })
    };

    if (description) {
      searchLanguages(description, cancelTokenSource)
          .then(response => {
            this.setState({
              foundBillTexts: response
            });
          })
          .catch(noop)
          .finally(onFinally);
    } else {
      getAllLanguages(cancelTokenSource)
          .then(response => {
            this.setState({
              foundBillTexts: response
            });
          })
          .catch(noop)
          .finally(onFinally);
    }

    this.hideModal();
  };

  deleteLanguage = (id: number): void => {
    const {foundBillTexts, cancelTokenSource} = this.state;

    deleteLanguage(id, cancelTokenSource)
        .then(() => {
          this.setState({
            foundBillTexts: foundBillTexts.filter(language => language.id != id)
          })
        })
        .catch(noop);
  };

  handleChange = debounce(({values}: BillTextsFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {description} = values;
    const {cancelTokenSource} = this.state;

    const onSuccess = (response: Array<LanguageDto>) => {
      this.setState({
        foundBillTexts: response,
        searchValues: values
      });
    };

    const onFinally = () => {
      this.setState({
        billTextsLoaded: true
      })
    };

    this.setState({
      billTextsLoaded: false
    });

    if (description) {
      searchLanguages(description, cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    } else {
      getAllLanguages(cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    }
  }, 300);

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {billTextsLoaded} = this.state;

    return (
        <PraxStyledSegment>
          <div>
            {this.renderSearchLanguagesSegment()}
          </div>

          <StyledViewTableRow>
            {!billTextsLoaded ?
                <LoaderComponent message={getMessage("language.loading")}/> :
                this.renderBillTextSegment()
            }
          </StyledViewTableRow>

          {this.renderUpsertBillTextModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchLanguagesSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundBillTexts} = this.state;

    const numResults = foundBillTexts.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  renderBillTextSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundBillTexts} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundBillTexts.length}
            rowGetter={this.languageRowGetter}
            rowRenderer={this.foundBillTextsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  languageRowGetter = ({index}: any) => {
    const {foundBillTexts} = this.state;

    Object.assign(foundBillTexts[index], {index: index + 1});

    return foundBillTexts[index];
  };

  foundBillTextsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundBillTexts} = this.state;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundBillTexts[index].id)}
        >
          {columns}
        </div>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("language.confirmDelete")}
              onConfirm={() => this.deleteLanguage(cellData)}
          />
        </React.Fragment>
    );
  };

  renderUpsertBillTextModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, language, languageId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={this.hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("language")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertBillTextForm
                language={language}
                languageId={languageId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

}

export default withMessages(BillTextView);
